﻿const BASE = '/api';

function getToken(): string | null {
  return localStorage.getItem('babuba_token');
}

// ── Voice notes: record in the dashboard → transcribe (local faster-whisper) ──
export async function transcribeAudio(blob: Blob, language?: string) {
  const token = getToken();
  const fd = new FormData();
  fd.append('audio', blob, 'voice.webm');
  if (language) fd.append('language', language);
  const res = await fetch(`${BASE}/chat/stt`, {
    method: 'POST',
    body: fd,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as any)?.error || `HTTP ${res.status}`);
  return data as { ok: boolean; text: string; language?: string | null; seconds?: number | null };
}

export async function request<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  if (!headers['Content-Type']) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;

  // Normalize: some callers pass '/api/...' already â€” avoid '/api/api/...' 404s
  const url = path.startsWith('/api/') ? path : `${BASE}${path}`;
  const res = await fetch(url, { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}

// ── Private feature flags (platform operator only) ──
export async function getConfig() {
  return request<{ raw: string; parsed: any }>('/config');
}
export async function setClientChat(enabled: boolean) {
  return request<{ success: boolean; enabled: boolean; restartRequired: boolean }>(
    '/config/features/clientChat',
    { method: 'POST', body: JSON.stringify({ enabled }) }
  );
}
export async function restartGateway() {
  return request<{ success: boolean; restarting: boolean; message: string }>(
    '/system/restart',
    { method: 'POST', body: JSON.stringify({}) }
  );
}

// â”€â”€ Auth â”€â”€
export async function login(email: string, password: string) {
  const token = getToken();
  if (token) localStorage.removeItem('babuba_token'); // clear stale token
  const res = await fetch(`${BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  if (data.token) localStorage.setItem('babuba_token', data.token);
  return data;
}

export function logout() {
  localStorage.removeItem('babuba_token');
}

// â”€â”€ Profile â”€â”€
export async function getProfile() {
  return request<any>('/admin/profile', { method: 'GET' });
}
export async function updateProfile(p: any) {
  return request('/admin/profile', { method: 'POST', body: JSON.stringify(p) });
}

// â”€â”€ Agents â”€â”€
export async function listAgents() {
  const r = await request<any>('/agents/list');
  return r.data || r.agents || [];
}
export async function createAgent(data: any) {
  return request('/agents/create', { method: 'POST', body: JSON.stringify(data) });
}
export async function updateAgent(id: string, data: any) {
  return request(`/agents/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}
export async function deleteAgent(id: string) {
  return request(`/agents/${id}`, { method: 'DELETE' });
}
export async function getAgentMemory(id: string) {
  const r = await request<any>(`/agents/${id}/memory`);
  return r.content || '';
}
export async function setAgentMemory(id: string, content: string) {
  return request(`/agents/${id}/memory`, { method: 'PUT', body: JSON.stringify({ content }) });
}
export async function getAgentSoul(id: string) {
  const r = await request<any>(`/agents/${id}/soul`);
  return r.content || '';
}

// â”€â”€ Per-Agent Monthly Budgets (cost control) â”€â”€
export async function listBudgets() {
  const r = await request<any>('/budgets');
  return r.budgets || [];
}
export async function setAgentBudget(agentId: string, monthlyTokens: number) {
  return request(`/budgets/${agentId}`, {
    method: 'POST',
    body: JSON.stringify({ monthlyTokens }),
  });
}
export async function setDashboardAccess(agentId: string, body: { enabled: boolean; password?: string }) {
  return request(`/agents/${agentId}/dashboard-access`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}
export async function suggestTools(purpose: string, agentName: string) {
  return request('/tools/suggest', {
    method: 'POST',
    body: JSON.stringify({ purpose, agentName }),
  });
}
export async function getToolCatalog() {
  const r = await request<any>('/tools/catalog');
  return r.tools || [];
}

// â”€â”€ Tool Library â”€â”€
export async function listToolsLibrary() {
  const r = await request<any>('/tools/library');
  return r.tools || [];
}
export async function createTool(data: any) {
  return request('/tools/library', { method: 'POST', body: JSON.stringify(data) });
}
export async function updateTool(id: string, data: any) {
  return request(`/tools/library/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function deleteTool(id: string) {
  return request(`/tools/library/${id}`, { method: 'DELETE' });
}
export async function assignToolToAgent(toolId: string, agentId: string, assigned: boolean) {
  return request(`/tools/library/${encodeURIComponent(toolId)}/assign`, {
    method: 'PATCH',
    body: JSON.stringify({ agentId, assigned }),
  });
}

export async function importFromGit(url: string) {
  return request('/tools/library/import', { method: 'POST', body: JSON.stringify({ url }) });
}

// â”€â”€ Skills â”€â”€
export async function listSkills() {
  const r = await request<any>('/skills');
  // skills API returns array directly
  return Array.isArray(r) ? r : (r.skills || []);
}
export async function createSkill(data: any) {
  return request('/skills', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateSkill(id: string, data: any) {
  return request(`/skills/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function deleteSkill(id: string) {
  return request(`/skills/${id}`, { method: 'DELETE' });
}
export async function assignSkillToAgent(skillId: string, agentId: string, assigned: boolean) {
  return request(`/skills/${encodeURIComponent(skillId)}/assign`, {
    method: 'PATCH',
    body: JSON.stringify({ agentId, assigned }),
  });
}

// â”€â”€ Chat â”€â”€
export async function superChat(message: string, files?: { name: string; data: string }[], clientId?: string) {
  return request('/chat/super', {
    method: 'POST',
    body: JSON.stringify({ message, files, clientId }),
  });
}

// ── Client registry ──
export async function listClients(): Promise<any[]> {
  const r = await request<any>('/clients');
  return r.data || [];
}
export function createClient(data: any) {
  return request('/clients', { method: 'POST', body: JSON.stringify(data) });
}
export function updateClient(id: string, data: any) {
  return request(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export function deleteClient(id: string) {
  return request(`/clients/${id}`, { method: 'DELETE' });
}

// ── Client private memory (per-customer MEMORY.md) ──
export async function getClientMemory(id: string): Promise<{ content: string; path?: string }> {
  const r = await request<any>(`/clients/${id}/memory`);
  return { content: r?.content || '', path: r?.path };
}
export function saveClientMemory(id: string, content: string) {
  return request(`/clients/${id}/memory`, { method: 'PUT', body: JSON.stringify({ content }) });
}

// ── GCP accounts (live gcloud auth list — monitoring identity picker) ──
export async function listGcloudAccounts(): Promise<{ accounts: { email: string; active: boolean }[]; activeAccount: string | null; available: boolean }> {
  const r = await request<any>('/gcloud/accounts');
  return r.data || { accounts: [], activeAccount: null, available: false };
}

// ── Super Chat History (polled to surface sub-agent completions live) ──
export async function superChatHistory(agentId?: string, clientId?: string): Promise<any[]> {
  const token = localStorage.getItem('babuba_token');
  const params = new URLSearchParams();
  if (agentId) params.set('agentId', agentId);
  if (clientId) params.set('clientId', clientId);
  const q = params.toString() ? `?${params.toString()}` : '';
  const res = await fetch(`${BASE}/chat/super/history${q}`, {
    headers: token ? { Authorization: "Bearer " + token } : {},
  });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.messages || [];
}

// ── Canvas list (Artifacts pane) ──
export async function superChatCanvases(): Promise<any[]> {
  const token = localStorage.getItem('babuba_token');
  try {
    const res = await fetch(`${BASE}/canvas/list`, {
      headers: token ? { Authorization: "Bearer " + token } : {},
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.canvases || [];
  } catch {
    return [];
  }
}

// â”€â”€ Streaming Super Chat (SSE) â”€â”€
// ── Document files list (Artifacts pane: PDF/DOCX/TXT/MD) ──
export async function superChatFiles(): Promise<any[]> {
  const token = localStorage.getItem('babuba_token');
  try {
    const res = await fetch(`${BASE}/canvas/files`, {
      headers: token ? { Authorization: "Bearer " + token } : {},
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.files || [];
  } catch {
    return [];
  }
}

export async function superChatStream(
  message: string,
  files: { name: string; data: string }[] | undefined,
  onToken: (token: string) => void,
  onGuardrail: (info: any) => void,
  onDone: (fullContent: string, files?: any[], durationMs?: number) => void,
  onError: (err: string) => void,
  onStep?: (step: { id: string; tool: string; message: string; status: string; duration?: string }) => void,
  signal?: AbortSignal,
  agentId?: string,
  onAbort?: () => void,
  onApproval?: (info: any) => void,
  onProgress?: (note: string) => void,
  clientId?: string
) {
  const token = localStorage.getItem('babuba_token');
  try {
    const res = await fetch(`${BASE}/chat/super/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: "Bearer " + token } : {}),
      },
      body: JSON.stringify({ message, files, agentId, clientId }),
      signal,
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      onError(errData.error || `HTTP ${res.status}`);
      return;
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';
    let receivedDone = false;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'token' && data.content) {
              fullContent += data.content;
              onToken(data.content);
            } else if (data.type === 'progress' && onProgress && data.content) {
              onProgress(data.content);
            } else if (data.type === 'guardrail') {
              onGuardrail(data);
            } else if (data.type === 'done') {
              receivedDone = true;
              onDone(data.content || fullContent, data.files, data.durationMs || 0);
            } else if (data.type === 'step' && onStep) {
              onStep(data);
            } else if (data.type === 'approval_pending' && onApproval) {
              onApproval(data);
            } else if (data.type === 'error') {
              onError(data.error);
            }
          } catch {}
        }
      }
    }

    // Stream ended WITHOUT a done event (e.g. connection dropped cleanly during a
    // server restart). Fire onError so the UI resets its loading state â€” otherwise
    // the send button stays silently dead until the page is reloaded.
    if (!receivedDone && !signal?.aborted) {
      onError('Connection to the server was lost mid-response. Please try again.');
    }
  } catch (err: any) {
    if (err?.name === 'AbortError' || signal?.aborted) {
      // User pressed Stop â€” let the UI finalize with partial content
      if (onAbort) onAbort();
      else onError('Stopped');
    } else {
      onError(err?.message || 'Request failed');
    }
  }
}

// â”€â”€ Approval actions (approve/reject a pending human-in-the-loop request) â”€â”€
export async function approvalAction(requestId: string, action: 'approve' | 'reject', comment?: string) {
  return request(`/approvals/${requestId}/action`, {
    method: 'POST',
    body: JSON.stringify({ action, comment }),
  });
}

export async function agentChat(agentId: string, message: string) {
  return request('/chat', { method: 'POST', body: JSON.stringify({ agentId, message }) });
}

// ── Streaming chat for department/agent pages ──
// Uses the streaming super-chat endpoint (works for any agent) so the UI can
// show live tokens AND a working indicator (onProgress narration).
export async function agentStreamChat(
  agentId: string,
  text: string,
  onToken: (token: string) => void,
  onProgress: (note: string) => void,
  onDone: (fullContent: string) => void,
  onError: (err: string) => void
) {
  const token = localStorage.getItem('babuba_token');
  try {
    const res = await fetch(`${BASE}/chat/super/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ message: text, agentId }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      onError(errData.error || `HTTP ${res.status}`);
      return;
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'token' && data.content) {
            fullContent += data.content;
            onToken(data.content);
          } else if (data.type === 'progress' && data.content) {
            onProgress(data.content);
          } else if (data.type === 'done') {
            onDone(fullContent || data.content || '');
          } else if (data.type === 'error') {
            onError(data.error || data.content || 'Request failed');
          }
        } catch {}
      }
    }
  } catch (err: any) {
    onError(err?.message || 'Request failed');
  }
}

// â”€â”€ Streaming Chat (SSE) â”€â”€
export async function streamChat(
  agentId: string,
  text: string,
  files: { name: string; data: string }[] | undefined,
  onToken: (token: string) => void,
  onToolCall: (tc: any) => void,
  onGuardrail: (info: any) => void,
  onDone: (fullContent: string) => void,
  onError: (err: string) => void,
  signal?: AbortSignal
) {
  const token = localStorage.getItem('babuba_token');
  const res = await fetch(`${BASE}/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ agentId, text, files }),
    signal,
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    onError(errData.error || `HTTP ${res.status}`);
    return;
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullContent = '';
  let receivedDone = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (data.type === 'token' && data.content) {
            fullContent += data.content;
            onToken(data.content);
          } else if (data.type === 'tool_call') {
            onToolCall(data.toolCall);
          } else if (data.type === 'guardrail') {
            onGuardrail(data);
          } else if (data.type === 'done') {
            receivedDone = true;
            onDone(data.content || fullContent);
          } else if (data.type === 'error') {
            onError(data.error || 'Unknown error');
          }
        } catch { /* skip malformed SSE */ }
      }
    }
  }

  // Stream ended without a done event â€” connection dropped cleanly.
  // Fire onError so the UI resets loading and the send button stays usable.
  if (!receivedDone && !signal?.aborted) {
    onError('Connection to the server was lost mid-response. Please try again.');
  }
}

// â”€â”€ Roles â”€â”€
export async function getRoles() {
  const r = await request<any>('/roles');
  return r.list || [];
}
export async function addRole(agentId: string, name: string, role: 'admin' | 'user', phone?: string, telegramId?: string, managedBy?: string) {
  return request(`/roles/${role}`, {
    method: 'POST',
    body: JSON.stringify({ agentId, name, phone, telegramId, managedBy }),
  });
}
export async function removeRole(agentId: string, phone: string) {
  return request(`/roles/${agentId}/${phone}`, { method: 'DELETE' });
}

// â”€â”€ Governance â”€â”€
export async function getGovernance() {
  return request<any>('/governance');
}
export async function saveGovernanceInstructions(agentId: string, instructions: string) {
  return request(`/governance/${agentId}`, {
    method: 'POST',
    body: JSON.stringify({ instructions }),
  });
}

// â”€â”€ Guardrail (Post-LLM Guardrail) â”€â”€
export async function getAgentGuardrailConfig(agentId: string) {
  return request<any>('/guardrail/agent/' + agentId);
}
export async function saveAgentGuardrailConfig(agentId: string, config: any, exemptions: string[]) {
  return request('/guardrail/agent/' + agentId, {
    method: 'POST',
    body: JSON.stringify({ config, exemptions }),
  });
}

// â”€â”€ Knowledge â”€â”€
export async function listKnowledge(agentId: string) {
  return request<any>(`/knowledge/${agentId}`);
}
export async function listRagIndex(agentId: string) {
  return request<any>(`/knowledge/${agentId}/rag`);
}
export async function deleteRagChunk(agentId: string, docId: string) {
  return request(`/knowledge/${agentId}/rag/${encodeURIComponent(docId)}`, { method: 'DELETE' });
}
export async function reindexKnowledge(agentId: string) {
  return request(`/knowledge/${agentId}/reindex`, { method: 'POST', body: JSON.stringify({}) });
}
export async function clearRagIndex(agentId: string) {
  return request(`/knowledge/${agentId}/rag/clear`, { method: 'POST', body: JSON.stringify({}) });
}
export async function knowledgeStatus() {
  return request<any>(`/knowledge/status`);
}
export async function uploadKnowledge(agentId: string, fileName: string, content: string) {
  return request(`/knowledge/${agentId}/upload`, {
    method: 'POST',
    body: JSON.stringify({ fileName, content }),
  });
}
export async function uploadKnowledgeFile(agentId: string, file: File) {
  const token = localStorage.getItem('babuba_token');
  const formData = new FormData();
  formData.append('file', file);
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${BASE}/knowledge/${agentId}/upload`, {
    method: 'POST',
    body: formData,
    headers,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);
  return data;
}
export async function deleteKnowledge(agentId: string, fileName: string) {
  return request(`/knowledge/${agentId}/${encodeURIComponent(fileName)}`, { method: 'DELETE' });
}
export async function searchKnowledge(agentId: string, query: string) {
  return request(`/knowledge/${agentId}/search`, {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
}

// â”€â”€ Cron â”€â”€
export async function listCronJobs() {
  const r = await request<any>('/cron');
  return r.jobs || [];
}
export async function createCronJob(data: any) {
  return request('/cron', { method: 'POST', body: JSON.stringify(data) });
}
export async function updateCronJob(id: string, data: any) {
  return request(`/cron/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function deleteCronJob(id: string) {
  return request(`/cron/${id}`, { method: 'DELETE' });
}
export async function runCronJob(id: string) {
  return request(`/cron/${id}/run`, { method: 'POST' });
}

// â”€â”€ Channels â”€â”€
export async function getChannelStatus() {
  return request<any>('/channels/status');
}
export async function connectTelegram(botToken: string) {
  return request('/channels/telegram', {
    method: 'POST',
    body: JSON.stringify({ botToken, enabled: true }),
  });
}
export async function logoutTelegram() {
  return request<any>('/channels/telegram/disconnect', { method: 'POST' });
}
export async function connectWhatsApp(config: any) {
  return request('/channels/whatsapp', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}
export async function connectGmail(config: any) {
  return request('/channels/gmail', {
    method: 'POST',
    body: JSON.stringify(config),
  });
}
export async function getGmailAuthUrl(clientId: string, clientSecret: string, redirectUri?: string) {
  return request<any>('/channels/gmail/auth-url', {
    method: 'POST',
    body: JSON.stringify({ clientId, clientSecret, redirectUri }),
  });
}
export async function exchangeGmailCode(code: string, state: string) {
  return request<any>('/channels/gmail/callback', {
    method: 'POST',
    body: JSON.stringify({ code, state }),
  });
}
export async function logoutGmail() {
  return request<any>('/channels/gmail/disconnect', { method: 'POST' });
}

// â”€â”€ WhatsApp Web (QR) â”€â”€
export async function initWhatsAppWeb() {
  return request<any>('/channels/whatsapp-web/init', { method: 'POST' });
}
export async function getWhatsAppWebStatus() {
  return request<any>('/channels/whatsapp-web/status');
}
export async function getWhatsAppWebQR() {
  return request<any>('/channels/whatsapp-web/qr');
}
export async function logoutWhatsAppWebClient() {
  return request<any>('/channels/whatsapp-web/logout', { method: 'POST' });
}

// â”€â”€ Permissions â”€â”€
export async function getPermissions() {
  const r = await request<any>('/admin/permissions');
  return r.permissions || {};
}
export async function updatePermissions(perms: Record<string, boolean>) {
  return request('/admin/permissions', {
    method: 'PUT',
    body: JSON.stringify({ permissions: perms }),
  });
}


// â”€â”€ Generic API helpers

export function apiGet(path: string) { return request(path); }
export function apiPost(path: string, body: any) { return request(path, { method: "POST", body: JSON.stringify(body) }); }
export function apiPut(path: string, body?: any) { return request(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }); }
export function apiDelete(path: string) { return request(path, { method: "DELETE" }); }

// â”€â”€ MCP â”€â”€
export async function listMCPClients() {
  const r = await request<any>('/mcp/clients');
  return r.data || [];
}
export async function getMCPClientTools(id: string) {
  const r = await request<any>(`/mcp/clients/${id}/tools`);
  return r.data || [];
}
export async function connectMCPClient(config: { id: string; transport: string; command?: string; args?: string[]; url?: string; apiKey?: string }) {
  return request('/mcp/clients', { method: 'POST', body: JSON.stringify(config) });
}
export async function callMCPTool(clientId: string, tool: string, args: Record<string, unknown> = {}) {
  return request(`/mcp/clients/${clientId}/call`, { method: 'POST', body: JSON.stringify({ tool, args }) });
}
export async function disconnectMCPClient(id: string) {
  return request(`/mcp/clients/${id}`, { method: 'DELETE' });
}
/** Configured MCP clients (with enabled flag + running status) — for toggles. */
export async function getMCPConfig() {
  const r = await request<any>('/mcp/config');
  return r.data || { enabled: true, clients: [] };
}
/** Enable/disable a configured MCP client (persists + connects/disconnects live). */
export async function setMCPClientEnabled(id: string, enabled: boolean) {
  return request(`/mcp/clients/${id}/enabled`, { method: 'PUT', body: JSON.stringify({ enabled }) });
}
export async function listMCPServers() {
  const r = await request<any>('/mcp/servers');
  return r.data || [];
}
export async function createMCPServer(config: { id: string; name: string; version?: string; transport?: string; port?: number; includeTools?: string[]; exposeAgent?: string }) {
  return request('/mcp/servers', { method: 'POST', body: JSON.stringify(config) });
}
export async function stopMCPServer(id: string) {
  return request(`/mcp/servers/${id}`, { method: 'DELETE' });
}

// â”€â”€ Runtime Model Config â”€â”€
export async function getModelConfig() {
  return request<any>('/config/super/model');
}
export async function setModelConfig(cfg: { model?: 'flash' | 'pro' | 'vision'; thinking?: 'high' | 'low' | 'off' }) {
  return request<any>('/config/super/model', { method: 'POST', body: JSON.stringify(cfg) });
}

// â”€â”€ LLM Provider (API key management) â”€â”€
export async function getLLMStatus() {
  return request<any>('/llm/status');
}
export async function saveLLMConfig(cfg: { provider: string; providerId?: string; apiKey?: string; model?: string; baseUrl?: string; vision?: boolean; activate?: boolean }) {
  return request<any>('/config/llm', { method: 'POST', body: JSON.stringify(cfg) });
}
export async function getLLMProviders() {
  return request<any>('/config/llm/providers');
}
export async function testVisionProvider(providerId: string) {
  return request<any>('/config/llm/test', { method: 'POST', body: JSON.stringify({ providerId }) });
}

// -- Watchdog --
export async function getWatchdog() {
  return request<any>('/watchdog', { method: 'GET' });
}


// -- Self-Update (in-app Update button) --
export async function getUpdateStatus() {
  return request<any>('/admin/update/status', { method: 'GET' });
}
export async function getUpdateChangelog() {
  return request<any>('/admin/update/changelog', { method: 'GET' });
}
export async function triggerUpdate() {
  return request<any>('/admin/update', { method: 'POST' });
}

// -- Logs --
export async function getLogs(level?: string, limit = 200, source?: string) {
  const q = new URLSearchParams();
  if (level) q.set('level', level);
  q.set('limit', String(limit));
  if (source) q.set('source', source);
  return request<any>(`/logs?${q.toString()}`);
}

// -- Super LLM config (merge-safe, masked keys) --
export async function getSuperLLMProviders() {
return request<any>('/config/super/llm');
}
export async function saveSuperLLMConfig(cfg: { provider: string; providerId?: string; apiKey?: string; model?: string; baseUrl?: string; vision?: boolean }) {
return request<any>('/config/super/llm', { method: 'POST', body: JSON.stringify(cfg) });
}
export async function deleteSuperLLMProvider(providerId: string) {
return request<any>('/config/super/llm/' + encodeURIComponent(providerId), { method: 'DELETE' });
}

export async function getRoleTabs() {
  return request<{
    roleTabs: Record<string, string[]>;
    agentRoleTabs: Record<string, Record<string, string[]>>;
    agents: { id: string; name: string }[];
    tabs: string[];
    roles: string[];
    effectiveTabs: string[];
  }>('/dashboard/role-tabs');
}

export async function setRoleTabs(roleTabs: Record<string, string[]>, agentRoleTabs?: Record<string, Record<string, string[]>>) {
  return request('/dashboard/role-tabs', {
    method: 'PUT',
    body: JSON.stringify({ roleTabs, agentRoleTabs }),
  });
}

// ── Self-Improve (telemetry + proposals) ──
export async function getSelfImproveErrors(hours = 24, agentId?: string, grouped = true) {
  const q = new URLSearchParams();
  q.set('hours', String(hours));
  if (agentId) q.set('agentId', agentId);
  q.set('grouped', String(grouped));
  return request<any>(`/selfimprove/errors?${q.toString()}`);
}
export async function getSelfImproveUsage(hours = 24) {
  return request<any>(`/selfimprove/usage?hours=${hours}`);
}
export async function getSelfImproveSummary() {
  return request<any>('/selfimprove/summary');
}
export async function getSelfImproveStatus() {
  return request<any>('/selfimprove/status');
}
export async function postSelfImproveBeacon(payload: { page?: string; tool?: string; action?: string }) {
  return request<any>('/selfimprove/beacon', { method: 'POST', body: JSON.stringify(payload) });
}
export async function runSelfImproveDiagnose(hours = 24) {
  return request<any>('/selfimprove/diagnose', { method: 'POST', body: JSON.stringify({ hours }) });
}
export async function getSelfImproveProposals(status?: string) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return request<any>(`/selfimprove/proposals${q}`);
}
export async function createSelfImproveProposal(data: any) {
  return request<any>('/selfimprove/proposals', { method: 'POST', body: JSON.stringify(data) });
}
export async function approveSelfImproveProposal(id: string) {
  return request<any>(`/selfimprove/proposals/${encodeURIComponent(id)}/approve`, { method: 'POST', body: JSON.stringify({}) });
}
export async function rejectSelfImproveProposal(id: string) {
  return request<any>(`/selfimprove/proposals/${encodeURIComponent(id)}/reject`, { method: 'POST', body: JSON.stringify({}) });
}
export async function deleteSelfImproveProposal(id: string) {
  return request<any>(`/selfimprove/proposals/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ── Per-agent dashboards (state/agents/<id>/dashboard.json) ──
export interface AgentDashboardSummary {
  agentId: string;
  title: string;
  subtitle?: string;
  brand?: string;
  provider?: string;
  canvasId?: string | null;
}

export async function getMyAgentDashboards() {
  return request<{ dashboards: AgentDashboardSummary[] }>('/agent-dashboard/mine');
}

export async function getAgentDashboard(agentId: string) {
  return request<{ spec: any; data: any }>('/agent-dashboard/' + encodeURIComponent(agentId));
}

export async function refreshAgentDashboard(agentId: string) {
  return request<{ ok: boolean; message: string }>(
    `/agent-dashboard/${encodeURIComponent(agentId)}/refresh`,
    { method: 'POST', body: JSON.stringify({}) }
  );
}

// ── Detached runs: the gateway keeps a run alive when the tab/page goes away.
// Stop must be explicit; inflight tells us a run is still going so we can re-attach. ──
export async function superChatStop(agentId?: string, clientId?: string): Promise<void> {
  const token = getToken();
  try {
    await fetch(`${BASE}/chat/super/stop`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      body: JSON.stringify({ agentId, clientId }),
      keepalive: true,
    });
  } catch { /* best effort */ }
}

export async function superChatInflight(agentId?: string, clientId?: string): Promise<{ running: boolean; startedAt?: number | null }> {
  const token = getToken();
  const qs = new URLSearchParams();
  if (agentId) qs.set('agentId', agentId);
  if (clientId) qs.set('clientId', clientId);
  try {
    const res = await fetch(`${BASE}/chat/super/inflight?${qs.toString()}`, {
      headers: token ? { Authorization: 'Bearer ' + token } : {},
    });
    if (!res.ok) return { running: false };
    return await res.json();
  } catch {
    return { running: false };
  }
}
